import { useEffect, useState } from 'react';
import { Calendar, Check, ChevronDown } from 'lucide-react';

import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { DATE_RANGE_OPTIONS, MAX_RANGE_LABEL, earliestFromFor, isoDate, today } from '@/hooks/useDateRange';
import { cn } from '@/lib/utils';

const PILL_BUTTON_CLASS =
	'inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3.5 py-2 text-sm font-semibold text-foreground transition hover:bg-muted/50';

/**
 * Preset list plus a free-form window. Native `<input type="date">` gives a real
 * calendar in every browser we support without pulling in a datepicker library —
 * the NearSt one (`react-date-range`) can't be used here because it arrives via
 * `@nearst/ui`, which is banned in this dashboard.
 */
function DateRangeMenu({ range }) {
	const [open, setOpen] = useState(false);
	const [from, setFrom] = useState(range.from);
	const [to, setTo] = useState(range.to);
	const [error, setError] = useState(null);

	// Reopening the picker should always start from whatever is on screen, not
	// from whatever half-typed dates were left behind last time.
	useEffect(() => {
		if (!open) return;
		setFrom(range.from);
		setTo(range.to);
		setError(null);
	}, [open, range.from, range.to]);

	const maxDate = isoDate(today());
	// Tracks the To field, so moving the end date moves how far back the start
	// date may reach. The hook clamps the span anyway (a hand-edited URL never
	// reaches the picker); this stops the UI offering a date it would snap.
	const minFrom = earliestFromFor(to);

	const applyCustom = () => {
		if (!from || !to) {
			setError('Pick both a start and an end date.');
			return;
		}
		if (from > to) {
			setError('The start date must be on or before the end date.');
			return;
		}
		if (from < minFrom) {
			setError(`The longest range is ${MAX_RANGE_LABEL}.`);
			return;
		}
		setError(null);
		range.setCustomRange(from, to);
		setOpen(false);
	};

	const selectPreset = (value) => {
		range.setRange(value);
		setOpen(false);
	};

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger className={PILL_BUTTON_CLASS}>
				<Calendar className="h-4 w-4" /> {range.label}
				<ChevronDown className="h-4 w-4 text-muted-foreground" />
			</PopoverTrigger>
			<PopoverContent align="start" className="w-64 p-2">
				<div className="flex flex-col">
					{DATE_RANGE_OPTIONS.map((option) => (
						<button
							key={option.value}
							type="button"
							onClick={() => selectPreset(option.value)}
							className={cn(
								'flex items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition hover:bg-muted/60',
								option.value === range.value ? 'font-semibold text-foreground' : 'text-muted-foreground'
							)}
						>
							{option.label}
							{option.value === range.value ? <Check className="h-4 w-4" /> : null}
						</button>
					))}
				</div>

				<div className="mt-2 border-t border-border/60 pt-3">
					<p className="px-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Custom range</p>
					<div className="mt-2 flex flex-col gap-2 px-1">
						<label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
							From
							<input
								type="date"
								value={from}
								min={minFrom}
								max={to || maxDate}
								onChange={(e) => setFrom(e.target.value)}
								className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
							/>
						</label>
						<label className="flex flex-col gap-1 text-xs font-medium text-muted-foreground">
							To
							<input
								type="date"
								value={to}
								min={from || undefined}
								max={maxDate}
								onChange={(e) => setTo(e.target.value)}
								className="rounded-lg border border-border bg-background px-2.5 py-1.5 text-sm text-foreground"
							/>
						</label>
						<p className="text-xs text-muted-foreground">Up to {MAX_RANGE_LABEL} at a time.</p>
						{error ? <p className="text-xs font-medium text-destructive">{error}</p> : null}
						<button
							type="button"
							onClick={applyCustom}
							className="mt-1 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
						>
							Apply
						</button>
					</div>
				</div>
			</PopoverContent>
		</Popover>
	);
}

/**
 * Date range card — soft-gradient rounded surface wrapping the three
 * controls. Lives inside the "Results by channel" section above the
 * filter pills, mirroring the Lovable design.
 */
export function DateRangeCard({ range }) {
	return (
		<div className="flex flex-wrap items-center gap-3 rounded-2xl border border-border/60 bg-gradient-soft px-4 py-3 shadow-[0_1px_2px_rgba(15,23,42,0.04)] md:px-5 md:py-4">
			<span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Showing data for</span>
			<div className="flex flex-wrap items-center gap-2">
				<DateRangeMenu range={range} />
				<span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">vs</span>
				{/*
				 * The comparison and granularity controls only have one option each
				 * for now (we always compare to the immediate previous period and
				 * always aggregate daily). The dropdowns are kept so the page
				 * visually matches the Lovable design — and so adding "Same period
				 * last year" / "Weekly" / "Monthly" later doesn't change the layout.
				 */}
				<DropdownMenu>
					<DropdownMenuTrigger className={PILL_BUTTON_CLASS}>
						Previous period <ChevronDown className="h-4 w-4 text-muted-foreground" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start">
						<DropdownMenuItem className="font-semibold">Previous period</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
				<DropdownMenu>
					<DropdownMenuTrigger className={PILL_BUTTON_CLASS}>
						Daily <ChevronDown className="h-4 w-4 text-muted-foreground" />
					</DropdownMenuTrigger>
					<DropdownMenuContent align="start">
						<DropdownMenuItem className="font-semibold">Daily</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			</div>
		</div>
	);
}
